// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// Pipeline tab for the app-detail page. Displays a stats band (last run, avg
// duration, success rate) derived from the loaded page of pipeline runs, then a
// restyled DataList of recent runs inside an .ad-group surface. No actor or
// check-count columns — neither field exists on the PipelineRun contract shape
// (listRuns 200 schema; PipelineRun type in @greenlight/shared).
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { AD_GROUP, AD_STAT } from '@/components/greenlight/surfaces';
import { pipelineMeta } from '@/components/greenlight/appStatusMeta';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { isTransientQueryError, liveQueryOptions, statusPollInterval } from '@/lib/polling';
import type { PipelineRun } from '@greenlight/shared';

import { AdEyebrow, TabErrorPanel, TabInlineEmpty, TabLoadingRows } from './tab-primitives';

// ── Stat derivation ───────────────────────────────────────────────────────────

interface PipelineStats {
  lastRun: PipelineRun | null;
  avgDurationMs: number | null;
  successPercent: number | null;
}

export function deriveStats(runs: PipelineRun[]): PipelineStats {
  const lastRun = runs[0] ?? null;

  // Avg duration over completed runs with both timestamps.
  // Exclude runs whose computed duration is NaN or negative (clock skew /
  // malformed timestamps) so they don't poison the average.
  const completed = runs
    .filter(
      (r): r is PipelineRun & { completedAt: string } =>
        r.completedAt !== null && r.triggeredAt !== null,
    )
    .filter((r) => {
      const d = new Date(r.completedAt).getTime() - new Date(r.triggeredAt).getTime();
      return !isNaN(d) && d >= 0;
    });
  const avgDurationMs =
    completed.length > 0
      ? completed.reduce((sum, r) => {
          return sum + (new Date(r.completedAt).getTime() - new Date(r.triggeredAt).getTime());
        }, 0) / completed.length
      : null;

  // Success % over terminal runs (passed + failed + cancelled).
  const terminal = runs.filter(
    (r) => r.status === 'passed' || r.status === 'failed' || r.status === 'cancelled',
  );
  const successPercent =
    terminal.length > 0
      ? Math.round((terminal.filter((r) => r.status === 'passed').length / terminal.length) * 100)
      : null;

  return { lastRun, avgDurationMs, successPercent };
}

export function formatDuration(ms: number): { value: string; unit: string } {
  if (ms < 60_000) return { value: Math.round(ms / 1000).toString(), unit: 's' };
  return { value: Math.round(ms / 60_000).toString(), unit: 'm' };
}

// ── Stats band ────────────────────────────────────────────────────────────────

function StatsBand({ stats }: { stats: PipelineStats }) {
  const { lastRun, avgDurationMs, successPercent } = stats;

  const cells: React.ReactNode[] = [];

  // LAST RUN tile — always shown when there is at least one run.
  if (lastRun !== null) {
    const meta = pipelineMeta(lastRun.status);
    cells.push(
      <div key="last-run" className={cn(AD_STAT, 'flex flex-col gap-1.5')}>
        <AdEyebrow>Last run</AdEyebrow>
        <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
        <p className="text-xs text-muted-foreground">{relativeTime(lastRun.triggeredAt)}</p>
      </div>,
    );
  }

  // AVG DURATION tile — shown when at least one completed run exists.
  if (avgDurationMs !== null) {
    const { value, unit } = formatDuration(avgDurationMs);
    cells.push(
      <div key="avg-duration" className={cn(AD_STAT, 'flex flex-col gap-1.5')}>
        <AdEyebrow>Avg duration</AdEyebrow>
        <p className="leading-none">
          <span className="text-[30px] font-extrabold leading-none tracking-tight tabular-nums text-foreground">
            {value}
          </span>
          <span className="ml-0.5 text-sm text-muted-foreground">{unit}</span>
        </p>
      </div>,
    );
  }

  // SUCCESS · RECENT tile — shown when terminal runs exist.
  if (successPercent !== null) {
    cells.push(
      <div key="success" className={cn(AD_STAT, 'flex flex-col gap-1.5')}>
        <AdEyebrow>Success · recent</AdEyebrow>
        <p className="leading-none">
          <span className="text-[30px] font-extrabold leading-none tracking-tight tabular-nums text-foreground">
            {successPercent}
          </span>
          <span className="ml-0.5 text-sm text-muted-foreground">%</span>
        </p>
      </div>,
    );
  }

  if (cells.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-3 sm:flex-row [&>*]:flex-1"
      data-testid="pipeline-stats-band"
      aria-label="Pipeline stats"
    >
      {cells}
    </div>
  );
}

// ── Table columns ─────────────────────────────────────────────────────────────

// Columns: COMMIT (link mono) · STATUS · RELEASE NOTES (first line) · BRANCH · WHEN.
// Actor and check-count columns are absent — neither field exists on PipelineRun
// (no actor field, no check count on the list shape).
const PIPELINE_COLUMNS: DataListColumn<PipelineRun>[] = [
  {
    key: 'commit',
    header: 'Commit',
    primary: true,
    render: (r) => <MonoValue className="hover:underline">{r.commitSha.slice(0, 7)}</MonoValue>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => {
      const meta = pipelineMeta(r.status);
      return <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>;
    },
  },
  {
    key: 'release-notes',
    header: 'Release notes',
    render: (r) => {
      // Untrusted text (PR title): first line only, plain text, truncated.
      const firstLine = r.releaseNotes?.split('\n', 1)[0];
      return firstLine ? (
        <span className="block max-w-[36ch] truncate text-sm text-foreground" title={firstLine}>
          {firstLine}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      );
    },
  },
  {
    key: 'branch',
    header: 'Branch',
    render: (r) => <span className="text-sm text-muted-foreground">{r.branch}</span>,
  },
  {
    key: 'triggered',
    header: 'Triggered',
    priority: 'md',
    render: (r) => (
      <span className="text-sm text-muted-foreground" title={r.triggeredAt}>
        {relativeTime(r.triggeredAt)}
      </span>
    ),
  },
];

// ── PipelineTab ───────────────────────────────────────────────────────────────

export function PipelineTab({ appId }: { appId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = api.pipeline.listRuns.useQuery(
    ['runs', appId],
    { params: { id: appId } },
    {
      ...liveQueryOptions,
      queryKey: ['runs', appId],
      refetchInterval: ({ state }) =>
        statusPollInterval(state.data?.body.items.map((run) => run.status) ?? []),
    },
  );
  if (isLoading) return <TabLoadingRows />;
  if ((isError && !isTransientQueryError(error)) || !data || data.status !== 200)
    return <TabErrorPanel message="Couldn't load pipeline runs." />;
  const runs = data.body.items;
  const stats = deriveStats(runs);
  return (
    <div className="space-y-3" data-testid="pipeline-tab">
      {isError && <RefreshWarning />}
      <StatsBand stats={stats} />
      <div className={AD_GROUP}>
        <DataList
          ariaLabel="Pipeline runs"
          items={runs}
          columns={PIPELINE_COLUMNS}
          rowKey={(r) => r.id}
          onRowActivate={(r) => navigate(`/apps/${appId}/runs/${r.id}`)}
          // Whole row is the mouse target (onRowActivate); on md+ the focusable
          // link in this action cell carries the keyboard/AT path to the run.
          rowAction={(r) => (
            <Button asChild variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
              <Link to={`/apps/${appId}/runs/${r.id}`}>View</Link>
            </Button>
          )}
          empty={<TabInlineEmpty message="No pipeline runs yet." />}
        />
      </div>
    </div>
  );
}
